import { IconUserOff } from "@tabler/icons-react";
import type { ColumnDef } from "@tanstack/react-table";
import { Flex, Grid, Input, Typography } from "antd";
import { useState } from "react";
import { useMe, useSetUserDisabled } from "@/common/artemis-operations/members";
import { confirmDestructive } from "@/common/confirm";
import type { OrgUser } from "@/common/model";
import { useNotify } from "@/common/use-notify";
import { toLabel } from "@/common/utils";
import { CustomIcon, DateField, IdField } from "@/components/base";
import {
	ResponsiveTable,
	type ResponsiveTableProps,
} from "@/components/responsive-table";
import { UserStatusBadge } from "./user-badges";

const columns: ColumnDef<OrgUser>[] = [
	{
		accessorKey: "displayName",
		header: "Name",
		minSize: 150,
		cell: ({ row }) => (
			<Typography.Text strong ellipsis>
				{toLabel(row.original.displayName)}
			</Typography.Text>
		),
	},
	{
		accessorKey: "email",
		header: "Email",
		size: 250,
		cell: ({ row }) => (
			<Typography.Text ellipsis>{row.original.email}</Typography.Text>
		),
	},
	// {
	// 	accessorKey: "provider",
	// 	header: "Provider",
	// 	minSize: 100,
	// 	cell: ({ row }) => row.original.provider,
	// },
	{
		accessorKey: "id",
		header: "Id",
		size: 250,
		cell: ({ row }) => <IdField id={row.original.id} />,
	},
	{
		accessorKey: "createdAt",
		header: "Joined",
		size: 120,
		enableSorting: true,
		cell: ({ row }) => <DateField value={row.original.createdAt as string} />,
	},
	{
		accessorKey: "lastActivityAt",
		header: "Last Activity",
		size: 140,
		enableSorting: true,
		cell: ({ row }) => (
			<DateField value={row.original.lastActivityAt as string} />
		),
	},
	{
		accessorKey: "disabledAt",
		header: "Status",
		size: 120,
		cell: ({ row }) => <UserStatusBadge user={row.original} />,
	},
];

type UserTableProps = Omit<ResponsiveTableProps<OrgUser>, "columns"> & {
	/** Current server-side search text (email or display name). */
	search: string;
	/** Called when the search is committed; the parent re-fetches. */
	onSearchChange: (search: string) => void;
};

/**
 * Organization users table. Data and search state are owned by the parent; the search box drives a
 * server-side query, matching the audit table pattern.
 */
export function UserTable({
	search,
	onSearchChange,
	...props
}: UserTableProps) {
	const screens = Grid.useBreakpoint();
	const isCompact = !screens.lg;
	const [searchText, setSearchText] = useState(search);
	const setDisabled = useSetUserDisabled();
	const notify = useNotify();
	const { data: me } = useMe({ enabled: true });

	const confirmToggleDisabled = (user: OrgUser) => {
		const disabled = user.disabledAt != null;
		confirmDestructive({
			title: disabled ? "Re-enable this user?" : "Disable this user?",
			content: disabled
				? undefined
				: "They stop counting toward your active-user quota.",
			okText: disabled ? "Enable" : "Disable",
			danger: !disabled,
			onOk() {
				setDisabled.mutate(
					{ userId: user.id, disabled: !disabled },
					{
						onSuccess: () =>
							notify.success(disabled ? "User enabled." : "User disabled."),
						onError: (e) =>
							notify.error((e as Error).message || "Failed to update user."),
					},
				);
			},
		});
	};

	return (
		<ResponsiveTable<OrgUser>
			columns={columns}
			pageSize={50}
			rowActions={[
				{
					label: (user) => (user.disabledAt != null ? "Enable" : "Disable"),
					icon: <CustomIcon icon={IconUserOff} />,
					danger: (user) => user.disabledAt == null,
					// You cannot disable your own account (also enforced by the API).
					disabled: (user) => user.disabledAt == null && user.id === me?.id,
					onClick: confirmToggleDisabled,
				},
			]}
			filterBar={() => (
				<Input.Search
					placeholder="Search by name or email"
					allowClear
					style={{ width: isCompact ? "100%" : 260 }}
					value={searchText}
					onChange={(e) => setSearchText(e.target.value)}
					onSearch={(v) => onSearchChange(v)}
				/>
			)}
			compactRender={(user) => (
				<Flex justify="space-between" style={{ width: "100%" }}>
					<Typography.Text strong ellipsis>
						{user.displayName}{" "}
						<Typography.Text type="secondary">({user.email})</Typography.Text>
					</Typography.Text>
					<UserStatusBadge user={user} />
				</Flex>
			)}
			{...props}
		/>
	);
}
